import { useMemo, useRef, useState } from 'react'
import { useApp } from '../state/AppStore'
import { STAT_TYPES, orderStatTypes } from '../domain/statTypes'
import { resolveResult, resultLabel } from '../domain/results'
import { formatDate } from '../lib/date'
import {
  backupFilename,
  copyText,
  downloadText,
  eventsCsv,
  newPlayerCount,
  parseBackup,
  perGameCsv,
} from '../lib/exportData'
import type { Game, ID, Player, Season, StatTypeId } from '../domain/types'
import type { Repo } from '../data'
import type { Snapshot as PlayHQSnapshot } from '../data/snapshotTypes'

type Note = { kind: 'ok' | 'warn' | 'error'; text: string } | null

/**
 * Manage.
 *
 * Deliberately not a settings screen for its own sake. Most of what used to need
 * managing now arrives from PlayHQ, so this covers the three things that are
 * genuinely local: who the app thinks the squad is, which stats the grid offers,
 * and getting the data out.
 */
export function Manage() {
  const { players, games, currentSeason, snapshot, repo, updateSeason, updatePlayer, reload } = useApp()
  const [note, setNote] = useState<Note>(null)

  return (
    <div className="manage">
      {note && <div className={`manage__note manage__note--${note.kind}`}>{note.text}</div>}

      <div className="manage__cols">
        <div className="manage__col">
          <Squad players={players} updatePlayer={updatePlayer} setNote={setNote} />
          <StatTypes season={currentSeason} updateSeason={updateSeason} setNote={setNote} />
        </div>
        <div className="manage__col">
          <SeasonPanel snapshot={snapshot?.snapshot ?? null} lastSynced={snapshot?.fetchedAt ?? null} />
          <GamesPanel games={games} snapshot={snapshot?.snapshot ?? null} />
          <DataPanel repo={repo} players={players} snapshot={snapshot?.snapshot ?? null} reload={reload} setNote={setNote} />
        </div>
      </div>
    </div>
  )
}

// --- squad -------------------------------------------------------------------

function Squad({
  players,
  updatePlayer,
  setNote,
}: {
  players: Player[]
  updatePlayer(p: Player): Promise<void>
  setNote(n: Note): void
}) {
  const [editing, setEditing] = useState<ID | null>(null)
  const [draft, setDraft] = useState('')

  const squad = useMemo(
    () => [...players].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name)),
    [players],
  )

  const save = async (p: Player) => {
    const name = draft.trim()
    setEditing(null)
    if (!name || name === p.name) return
    await updatePlayer({ ...p, name })
    setNote({ kind: 'ok', text: `Renamed to ${name}. This is the name shown in this app only.` })
  }

  return (
    <section className="panel">
      <h2 className="panel__title">
        Squad <span className="count-badge">{players.filter((p) => p.active).length}</span>
      </h2>
      <p className="muted small">Comes from PlayHQ line-ups.</p>
      <div className="rows">
        {squad.map((p) => (
          <div key={p.id} className={'row' + (p.active ? '' : ' row--muted')}>
            {editing === p.id ? (
              <input
                className="row__input"
                value={draft}
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => void save(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void save(p)
                  if (e.key === 'Escape') setEditing(null)
                }}
              />
            ) : (
              <div className="row__label">
                <span className="row__title">{p.name}</span>
                <span className="row__meta">
                  {p.jerseyNumber ? `#${p.jerseyNumber}` : ''}
                  {p.isFillIn ? ' fill-in' : ''}
                  {p.needsNaming ? ' needs a name' : ''}
                  {!p.playhqProfileId && !p.isFillIn ? ' added by hand' : ''}
                </span>
              </div>
            )}
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                setDraft(p.name)
                setEditing(p.id)
              }}
            >
              Rename
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void updatePlayer({ ...p, active: !p.active })}
            >
              {p.active ? 'Archive' : 'Restore'}
            </button>
          </div>
        ))}
        {squad.length === 0 && <p className="muted">Nobody yet. The squad appears after the first sync.</p>}
      </div>
    </section>
  )
}

// --- stat types --------------------------------------------------------------

function StatTypes({
  season,
  updateSeason,
  setNote,
}: {
  season: Season
  updateSeason(s: Season): Promise<void>
  setNote(n: Note): void
}) {
  const enabled = new Set(orderStatTypes(season.enabledStatTypes))

  const toggle = async (id: StatTypeId) => {
    const next = new Set(enabled)
    if (next.has(id)) {
      if (next.size === 1) {
        setNote({ kind: 'warn', text: 'Keep at least one stat: the live grid needs a column.' })
        return
      }
      next.delete(id)
    } else {
      next.add(id)
    }
    await updateSeason({ ...season, enabledStatTypes: orderStatTypes([...next]) })
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Stats tracked</h2>
      <p className="muted small">
        Each one adds a column to the live grid. More columns mean narrower buttons, so only turn on what
        you will actually tap.
      </p>
      <div className="chip-grid">
        {STAT_TYPES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={'chip' + (enabled.has(s.id) ? ' chip--on' : '')}
            style={enabled.has(s.id) ? undefined : { borderColor: s.color, color: s.color }}
            onClick={() => void toggle(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
    </section>
  )
}

// --- season ------------------------------------------------------------------

function SeasonPanel({
  snapshot,
  lastSynced,
}: {
  snapshot: PlayHQSnapshot | null
  lastSynced: number | null
}) {
  if (!snapshot) {
    return (
      <section className="panel">
        <h2 className="panel__title">Season</h2>
        <p className="muted">No fixture data yet.</p>
      </section>
    )
  }
  const s = snapshot.season
  const ladder = snapshot.ladder.find((r) => r.is_us)
  return (
    <section className="panel">
      <h2 className="panel__title">Season</h2>
      <dl className="facts">
        <dt>Grade</dt>
        <dd>{s.gradeName ?? s.name}</dd>
        <dt>Format</dt>
        <dd>{s.periodFormat === 'quarters' ? 'Four quarters' : 'Two halves'}</dd>
        <dt>Games</dt>
        <dd>{snapshot.games.length}</dd>
        <dt>Byes</dt>
        <dd>{snapshot.byes.map((b) => b.round_abbreviation).join(', ') || 'none'}</dd>
        {ladder && (
          <>
            <dt>Ladder</dt>
            <dd>
              {ladder.position} of {snapshot.ladder.length} · {ladder.won}W {ladder.lost}L
            </dd>
          </>
        )}
        <dt>Synced</dt>
        <dd>{lastSynced ? formatDate(new Date(lastSynced).toISOString().slice(0, 10)) : 'never'}</dd>
      </dl>
      <p className="muted small">
        Rounds up to {s.gradingRoundsThrough} are grading rounds, excluded from the PlayHQ ladder. They
        still count in our stats.
      </p>
    </section>
  )
}

// --- games -------------------------------------------------------------------

function GamesPanel({
  games,
  snapshot,
}: {
  games: Game[]
  snapshot: PlayHQSnapshot | null
}) {
  const scored = useMemo(
    () => [...games].filter((g) => g.status === 'final').sort((a, b) => b.date.localeCompare(a.date)),
    [games],
  )
  return (
    <section className="panel">
      <h2 className="panel__title">
        Games scored <span className="count-badge">{scored.length}</span>
      </h2>
      <div className="rows">
        {scored.slice(0, 8).map((g) => {
          const r = resolveResult(g, snapshot)
          return (
            <div key={g.id} className="row">
              <div className="row__label">
                <span className="row__title">
                  {g.roundAbbreviation ? `${g.roundAbbreviation} ` : ''}
                  {g.homeAway === 'home' ? 'vs' : '@'} {g.opponentName ?? 'Opponent'}
                </span>
                <span className="row__meta">{formatDate(g.date)}</span>
              </div>
              <span className={'pill' + (r.awaitingResult ? ' pill--pending' : '')}>{resultLabel(r)}</span>
            </div>
          )
        })}
        {scored.length === 0 && <p className="muted">No completed games yet.</p>}
      </div>
    </section>
  )
}

// --- data --------------------------------------------------------------------

function DataPanel({
  repo,
  players,
  snapshot,
  reload,
  setNote,
}: {
  repo: Repo
  players: Player[]
  snapshot: PlayHQSnapshot | null
  reload(): Promise<void>
  setNote(n: Note): void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [lastExport, setLastExport] = useState<{ filename: string; text: string } | null>(null)

  /**
   * A browser gives no confirmation that a download actually landed -- the click
   * either throws or it does not. So this says what it genuinely knows ("started")
   * rather than "saved", and tells the scorer where to look. Copy is offered
   * alongside rather than only as a fallback, because on iOS in standalone mode
   * it is frequently the one that works.
   */
  const emit = async (filename: string, text: string, mime: string) => {
    if (downloadText(filename, text, mime)) {
      setLastExport({ filename, text })
      setNote({
        kind: 'ok',
        text: `Download started: ${filename}. If it does not appear, use Copy below.`,
      })
      return
    }
    const copied = await copyText(text)
    setNote(
      copied
        ? { kind: 'warn', text: 'The browser blocked the download, so it is on the clipboard instead. Paste it somewhere safe.' }
        : { kind: 'error', text: 'Could not download or copy. Try opening the app in a browser tab rather than from the home screen.' },
    )
  }

  const exportJson = async () => {
    setBusy(true)
    try {
      const backup = await repo.exportAll()
      await emit(backupFilename('hustle-backup', 'json'), JSON.stringify(backup, null, 2), 'application/json')
    } finally {
      setBusy(false)
    }
  }

  const exportCsv = async (kind: 'events' | 'pergame') => {
    setBusy(true)
    try {
      const backup = await repo.exportAll()
      const text = kind === 'events' ? eventsCsv(backup, snapshot) : perGameCsv(backup, snapshot)
      await emit(backupFilename(kind === 'events' ? 'hustle-events' : 'hustle-per-game', 'csv'), text, 'text/csv')
    } finally {
      setBusy(false)
    }
  }

  const importJson = async (file: File) => {
    setBusy(true)
    try {
      const { backup, summary } = parseBackup(await file.text())
      const fresh = newPlayerCount(backup.players, players)
      await repo.importAll(backup)
      await reload()
      setNote({
        kind: 'ok',
        text:
          `Imported ${summary.events} stats across ${summary.games} games, ` +
          `${summary.players} players (${fresh} new). Records with matching ids were updated, not duplicated.`,
      })
    } catch (err) {
      setNote({ kind: 'error', text: `Import failed: ${(err as Error).message}` })
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const reset = async () => {
    setBusy(true)
    try {
      await repo.clearAll()
      await reload()
      setConfirmReset(false)
      setNote({ kind: 'ok', text: 'Local data cleared. Fixtures and squad will reload from the server.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Data</h2>
      <p className="muted small">Import and export data for backup.</p>

      <div className="manage__actions">
        <button type="button" className="btn" disabled={busy} onClick={() => void exportJson()}>
          Export backup (JSON)
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void exportCsv('pergame')}>
          Export per game (CSV)
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void exportCsv('events')}>
          Export every stat (CSV)
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          Import backup
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void importJson(f)
          }}
        />
      </div>

      {lastExport && (
        <div className="manage__copy">
          <span className="muted small">Did not get {lastExport.filename}?</span>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() =>
              void copyText(lastExport.text).then((ok) =>
                setNote(
                  ok
                    ? { kind: 'ok', text: 'Copied. Paste it into a note or email to keep it safe.' }
                    : { kind: 'error', text: 'The browser would not allow copying.' },
                ),
              )
            }
          >
            Copy it instead
          </button>
        </div>
      )}

      <p className="muted small">
        Import merges by id: a record already here is updated, never duplicated. Nothing is deleted.
      </p>

      <div className="manage__danger">
        {confirmReset ? (
          <>
            <span className="muted small">
              Deletes every game and stat stored on this device. Export first if you have not.
            </span>
            <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void reset()}>
              Yes, erase it
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => setConfirmReset(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="btn btn--danger" onClick={() => setConfirmReset(true)}>
            Erase local data
          </button>
        )}
      </div>
    </section>
  )
}
